import websiteApi from '@api/websiteApi';
import appState, { appStatePersist } from '@appState';
import { t, useTranslator } from '@i18n';
import { Button, Group } from '@mantine/core';
import { modals } from '@mantine/modals';
import type { ChangelogVersion } from '@models/changelogVersion';
import isTruthy from '@util/isTruthy';
import { useEffect } from 'react';
import { getPossibleLocales } from 'schummar-translate';

const isEnabled = !config.TESTING || new URLSearchParams(window.location.search).has('changelog');

export default function ChangelogCheck() {
  useEffect(() => {
    if (!isEnabled) return;
    let stopped = false;

    websiteApi.changelog
      .get()
      .then(async (changelog) => {
        await appStatePersist.initialized;
        if (stopped) return;

        const appMinor = config.VERSION.split('.').slice(0, 2).join('.');
        const seenVersion = appState.get().seenChangelog;
        const version = changelog.find((version) => {
          const minor = version.id.split('.').slice(0, 2).join('.');
          return minor === appMinor;
        });

        if (version && version.id !== seenVersion) {
          modals.open({
            title: t('changelog.title', { version: version.id }),
            children: <ChangelogDialog {...version} />,
            centered: true,
            onClose,
          });

          function onClose() {
            appState.set('seenChangelog', version!.id);
          }
        }
      })
      .catch((error) => {
        if (stopped) return;
        console.error('Failed to fetch changelog:', error);
      });

    return () => {
      stopped = true;
    };
  }, []);

  return null;
}

function ChangelogDialog({ date, languages, summary, href }: ChangelogVersion) {
  const t = useTranslator();

  const possibleLocales = getPossibleLocales(t.locale);
  const language = possibleLocales.map((locale) => languages?.[locale]).find(isTruthy);
  const _summary = language?.summary ?? summary;
  const _href = language?.href ?? href ?? '';

  return (
    <div>
      <p
        css={{
          marginTop: '-1em',
          color: 'var(--mantine-color-dimmed)',
        }}
      >
        {t('changelog.date', { date: t.dateTimeFormat(date, { dateStyle: 'medium' }) })}
      </p>

      {_summary && <p>{_summary}</p>}

      <Group justify="flex-end">
        <Button variant="outline" component="a" href={_href} target="_blank" rel="noopener noreferrer">
          {t('changelog.link')}
        </Button>

        <Button onClick={() => modals.closeAll()}>{t('common.ok')}</Button>
      </Group>
    </div>
  );
}
